import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HandHeart, Sparkles, ArrowRight } from "lucide-react";

const ROTATE_MS = 5000;

/**
 * Rotador auto-rotativo de las ultimas 5 peticiones de oracion.
 * Auto-contenido: hace su propio fetch y maneja "Estoy orando por ti".
 * - refreshToken: cambia su valor para forzar refetch (ej. al publicar una peticion nueva)
 * - onPrayedSync: callback opcional (prayerId, prayCount) para sincronizar listas externas
 * - showLink: muestra enlace "Ver el muro completo"
 */
export const LatestPrayersRotator = ({ refreshToken = 0, onPrayedSync, showLink = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [latest, setLatest] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const rotateRef = useRef(null);

  const fetchLatest = useCallback(() => {
    api
      .get("/prayers?sort=recent&skip=0&limit=5")
      .then((res) => {
        setLatest(res.data.prayers || []);
        setIdx(0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest, refreshToken]);

  useEffect(() => {
    if (latest.length <= 1) return;
    rotateRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx((i) => (i + 1) % latest.length);
        setFading(false);
      }, 250);
    }, ROTATE_MS);
    return () => clearInterval(rotateRef.current);
  }, [latest.length]);

  const goTo = (i) => {
    setFading(true);
    setTimeout(() => {
      setIdx(i);
      setFading(false);
    }, 150);
  };

  const handlePray = async (prayer) => {
    if (!user) {
      toast.info("Inicia sesión para orar por esta petición");
      navigate("/login");
      return;
    }
    if (prayer.already_prayed) return;
    try {
      const res = await api.post(`/prayers/${prayer.id}/pray`);
      setLatest((prev) =>
        prev.map((p) =>
          p.id === prayer.id ? { ...p, pray_count: res.data.pray_count, already_prayed: true } : p
        )
      );
      onPrayedSync?.(prayer.id, res.data.pray_count);
      toast.success("Gracias. Estamos contigo en oración.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo registrar tu oración. Intenta de nuevo.");
    }
  };

  const current = latest[idx];
  if (!current) return null;

  return (
    <div data-testid="latest-prayers-rotator-wrap">
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Recién llegadas al muro
        </span>
        <div className="flex items-center gap-1.5" data-testid="latest-prayers-dots">
          {latest.map((p, i) => (
            <button
              key={p.id}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                i === idx ? "w-5 bg-primary" : "w-2 bg-border hover:bg-accent"
              }`}
              aria-label={`Petición ${i + 1}`}
            />
          ))}
        </div>
      </div>
      <div
        className={`flex flex-col gap-3 rounded-[var(--radius)] border border-border bg-card p-4 shadow-[var(--shadow-sm)] transition-opacity duration-300 sm:flex-row sm:items-center sm:justify-between ${fading ? "opacity-0" : "opacity-100"}`}
        data-testid="latest-prayer-rotator"
      >
        <div className="min-w-0">
          <p className="line-clamp-2 text-sm leading-relaxed text-foreground/90">“{current.texto}”</p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{current.user_name}</span>
            {" · "}
            {current.pray_count === 1 ? "1 orando" : `${current.pray_count} orando`}
          </p>
        </div>
        <Button
          size="sm"
          variant={current.already_prayed ? "secondary" : "default"}
          disabled={current.already_prayed}
          onClick={() => handlePray(current)}
          className="h-8 shrink-0 px-3 text-xs"
          data-testid="latest-prayer-pray-button"
        >
          <HandHeart className="mr-1.5 h-3.5 w-3.5" />
          {current.already_prayed ? "Orando" : "Estoy orando por ti"}
        </Button>
      </div>
      {showLink && (
        <div className="mt-3 text-right">
          <Link
            to="/oracion"
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
            data-testid="rotator-ver-muro-link"
          >
            Ver el muro completo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
};
